// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { getSecureStorage } from '../native/secure-storage.js';
import { resolveDefaultSyncUrl } from './apiConfig.js';

const TOKEN_KEY = 'hushold_token';
const REFRESH_KEY = 'hushold_refresh';
const KEY_DATA_KEY = 'hushold_keydata';
const USERNAME_KEY = 'hushold_username';
const SYNC_URL_KEY = 'hushold_syncurl';

export interface StoredSession {
  token: string;
  refreshToken: string;
  keyData: ArrayBuffer;
  username: string;
  syncUrl: string;
}

// DECISION: session storage is now routed through the secure-storage
// abstraction (native/secure-storage.ts). On web this is sessionStorage exactly
// as before; on native it is the OS Keychain/Keystore. Because Keychain reads
// are asynchronous, saveSession/loadSession/clearSession are now async. Pure,
// synchronous helpers (JWT decoding, expiry) stay synchronous.

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    const payloadPart = parts[1];
    if (parts.length !== 3 || !payloadPart) return null;
    const b64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const exp = payload['exp'];
  // A token with no expiry claim is treated as ALREADY expired now that the
  // server always issues short-lived, expiring access tokens (SEC-03): a token
  // lacking `exp` is malformed/legacy and must not be trusted indefinitely.
  if (typeof exp !== 'number') return true;
  return Date.now() / 1000 >= exp;
}

/** Absolute expiry of the access token in epoch-ms, or null if it has no exp claim. */
export function tokenExpiresAt(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.['exp'];
  return typeof exp === 'number' ? exp * 1000 : null;
}

function encodeKey(keyData: ArrayBuffer): string {
  const bytes = new Uint8Array(keyData);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function decodeKey(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export async function saveSession(
  token: string,
  refreshToken: string,
  keyData: ArrayBuffer,
  username: string,
  syncUrl: string,
): Promise<void> {
  const store = getSecureStorage();
  await Promise.all([
    store.set(TOKEN_KEY, token),
    store.set(REFRESH_KEY, refreshToken),
    store.set(USERNAME_KEY, username),
    store.set(SYNC_URL_KEY, syncUrl),
    store.set(KEY_DATA_KEY, encodeKey(keyData)),
  ]);
}

// Persist just the rotated access + refresh tokens after a silent refresh, without
// disturbing the key material or username.
export async function saveTokens(token: string, refreshToken: string): Promise<void> {
  const store = getSecureStorage();
  await Promise.all([store.set(TOKEN_KEY, token), store.set(REFRESH_KEY, refreshToken)]);
}

// Persist just the DEK after a key rotation (SEC-07), leaving tokens intact.
export async function saveKeyData(keyData: ArrayBuffer): Promise<void> {
  await getSecureStorage().set(KEY_DATA_KEY, encodeKey(keyData));
}

export async function clearSession(): Promise<void> {
  // DECISION: only the session secrets (tokens, key, username) are wiped. The
  // sync-server URL is configuration, not a credential — keeping it means a user
  // who logs out (or whose token expires, or a native app requiring login every
  // cold start) does not have to re-enter their server address every time.
  const store = getSecureStorage();
  await Promise.all([
    store.remove(TOKEN_KEY),
    store.remove(REFRESH_KEY),
    store.remove(KEY_DATA_KEY),
    store.remove(USERNAME_KEY),
  ]);
}

// Persist just the sync-server URL, independent of a full session. Used when the
// user edits the Server URL field before logging in.
export async function saveSyncUrl(syncUrl: string): Promise<void> {
  await getSecureStorage().set(SYNC_URL_KEY, syncUrl);
}

// Read the persisted sync-server URL (or null if none), without materializing a
// session. Used on bootstrap to seed a previously-configured server address.
export async function peekSyncUrl(): Promise<string | null> {
  return getSecureStorage().get(SYNC_URL_KEY);
}

export type LoadSessionResult = { session: StoredSession | null; expired: boolean };

export async function loadSession(): Promise<LoadSessionResult> {
  const store = getSecureStorage();
  const [token, refreshToken, keyB64, username, syncUrl] = await Promise.all([
    store.get(TOKEN_KEY),
    store.get(REFRESH_KEY),
    store.get(KEY_DATA_KEY),
    store.get(USERNAME_KEY),
    store.get(SYNC_URL_KEY),
  ]);

  if (!token || !refreshToken || !keyB64 || !username) {
    return { session: null, expired: false };
  }

  // A merely-expired access token is NOT fatal: the caller can exchange the
  // refresh token for a new one on bootstrap. We still surface the session; the
  // access token is refreshed reactively/proactively (see AppContext).
  try {
    return {
      session: {
        token,
        refreshToken,
        username,
        keyData: decodeKey(keyB64),
        syncUrl: syncUrl ?? resolveDefaultSyncUrl(),
      },
      expired: isTokenExpired(token),
    };
  } catch {
    await clearSession();
    return { session: null, expired: false };
  }
}

// Peek at whether a (non-expired) session is persisted without materializing the
// key into app state. Used on native cold start to decide whether to present the
// biometric lock screen. Returns the username so the lock screen can greet the
// user, or null if there is nothing to unlock.
export async function peekStoredUsername(): Promise<string | null> {
  const store = getSecureStorage();
  const [refreshToken, username] = await Promise.all([
    store.get(REFRESH_KEY),
    store.get(USERNAME_KEY),
  ]);
  // Presence of a refresh token (not the short-lived access token) determines
  // whether there is a session to unlock — the access token may have expired.
  if (!refreshToken || !username) return null;
  return username;
}
